import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Polygon } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  X, Loader2, Landmark, Map as MapIcon, ShieldAlert, Sparkles, ScrollText,
  FileDown, Building2, Ruler, CheckCircle2, AlertTriangle, ExternalLink, Info,
} from "lucide-react";

const NA = "Donnée officielle non disponible";
const parcelIcon = L.divIcon({
  className: "foncier-pin",
  html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#C6A87C;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 22],
});

// GeoJSON (lon,lat) -> Leaflet rings [[lat,lon],...]
const toRings = (geometry) => {
  if (!geometry) return [];
  const c = geometry.coordinates || [];
  if (geometry.type === "Polygon") return [c[0].map(([lon, lat]) => [lat, lon])];
  if (geometry.type === "MultiPolygon") return c.map((poly) => poly[0].map(([lon, lat]) => [lat, lon]));
  return [];
};
const ringsCenter = (rings) => {
  const pts = rings.flat();
  if (!pts.length) return null;
  const lat = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const lon = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  return [lat, lon];
};

const Sec = ({ icon: Icon, title, children, testid }) => (
  <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={testid}>
    <h3 className="mb-3 flex items-center gap-2 font-display text-base text-[#0F172A] sm:text-lg">
      <Icon className="h-4 w-4 shrink-0 text-[#C6A87C]" /> {title}
    </h3>
    {children}
  </section>
);

const KV = ({ label, value }) => (
  <div className="flex flex-col border-b border-slate-100 py-2 last:border-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <span className="text-xs font-medium text-slate-500 sm:w-1/2">{label}</span>
    <span className={`text-sm ${value == null || value === "" ? "italic text-slate-400" : "text-[#0F172A]"} sm:w-1/2 sm:text-right`}>
      {value == null || value === "" ? NA : value}
    </span>
  </div>
);

const POT = {
  favorable: { label: "Favorable", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  a_etudier: { label: "À étudier", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Info },
  defavorable: { label: "Défavorable", cls: "bg-slate-100 text-slate-500 border-slate-200", icon: AlertTriangle },
};
const PotPill = ({ label, level }) => {
  const p = POT[level] || { label: NA, cls: "bg-slate-50 text-slate-400 border-slate-200 italic", icon: Info };
  const Icon = p.icon;
  return (
    <div className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${p.cls}`}>
      <span className="font-medium text-[#0F172A]">{label}</span>
      <span className="flex items-center gap-1 text-xs font-semibold"><Icon className="h-3.5 w-3.5" /> {p.label}</span>
    </div>
  );
};

export default function FoncierSheet({ listing, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rules, setRules] = useState(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!listing?.id) return;
    setLoading(true); setError(null);
    api.get(`/radar/listings/${listing.id}/foncier`)
      .then((r) => { setData(r.data); if (r.data?.regles_ia) setRules(r.data.regles_ia); })
      .catch((e) => setError(e?.response?.data?.detail || "Analyse indisponible."))
      .finally(() => setLoading(false));
  }, [listing?.id]);

  const loadRules = async () => {
    setRulesLoading(true);
    try {
      const r = await api.post(`/radar/listings/${listing.id}/foncier/rules`);
      setRules(r.data);
      if (!r.data?.available) toast.info(r.data?.reason || "Règles non extractibles.");
    } catch (e) {
      toast.error("Extraction des règles impossible.");
    } finally { setRulesLoading(false); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/radar/listings/${listing.id}/foncier/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `RADAR-Foncier-${(listing.city || "bien").replace(/\s+/g, "-")}-${listing.id}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Fiche PDF téléchargée.");
    } catch (e) {
      toast.error("Téléchargement du PDF impossible.");
    } finally { setPdfLoading(false); }
  };

  const cad = data?.cadastre || {};
  const plu = data?.urbanisme || {};
  const risk = data?.risques || {};
  const sup = data?.servitudes || {};
  const pot = data?.potentiel || {};
  const score = data?.score || null;
  const _band = score?.band;
  const bandBg = _band === "fort" ? "bg-emerald-600" : _band === "a_etudier" ? "bg-amber-500" : "bg-rose-500";
  const bandText = _band === "fort" ? "text-emerald-700" : _band === "a_etudier" ? "text-amber-700" : "text-rose-700";
  const rings = toRings(cad.geometry);
  const center = ringsCenter(rings) || (data?.center ? [data.center.lat, data.center.lon] : null);

  const RULE_LABELS = {
    emprise_au_sol: "Emprise au sol", hauteur_max: "Hauteur maximale",
    retraits_alignements: "Retraits / alignements", stationnement: "Stationnement",
    espaces_verts: "Espaces verts", extension: "Extension",
    division: "Division", surelevation: "Surélévation", destinations_autorisees: "Destinations autorisées",
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-6" data-testid="foncier-sheet">
      <div className="min-h-full w-full max-w-4xl bg-slate-50 sm:min-h-0 sm:rounded-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur sm:rounded-t-2xl sm:px-6">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 truncate font-display text-base text-[#0F172A] sm:text-lg">
              <Landmark className="h-5 w-5 shrink-0 text-[#C6A87C]" />
              {t("radar.foncier.title", "Analyse foncière et urbanistique")}
            </h2>
            <p className="truncate text-xs text-slate-500">{listing.geo_label || listing.address || listing.city}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button onClick={downloadPdf} disabled={pdfLoading || loading || !!error} size="sm" variant="outline" className="rounded-full" data-testid="foncier-pdf-btn">
              {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              <span className="ml-1.5 hidden sm:inline">{t("radar.foncier.pdf", "Fiche PDF")}</span>
            </Button>
            <button onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" data-testid="foncier-close"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="space-y-4 p-4 sm:p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-3 py-24 text-slate-500" data-testid="foncier-loading">
              <Loader2 className="h-8 w-8 animate-spin text-[#C6A87C]" />
              <p className="text-sm">{t("radar.foncier.loading", "Analyse des sources officielles en cours…")}</p>
            </div>
          )}
          {error && !loading && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-800" data-testid="foncier-error">{error}</div>
          )}

          {data?.available && !loading && (
            <>
              {/* 0 · Score foncier synthétique /100 */}
              {score && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="foncier-score">
                  <div className="flex items-center gap-4">
                    <div className={`flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl text-white ${bandBg}`}>
                      <span className="font-display text-3xl leading-none" data-testid="foncier-score-value">{score.score}</span>
                      <span className="text-[10px] opacity-90">/100</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{t("radar.foncier.scoreTitle", "Score foncier")}</p>
                      <p className={`font-display text-lg ${bandText}`} data-testid="foncier-score-band">{t(`radar.foncier.band.${score.band}`, { defaultValue: score.band_label })}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500">{t("radar.foncier.scoreHint", "Note synthétique 0–100 basée sur des faits officiels (PLU, cadastre, Géorisques, DVF).")}</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2.5">
                    {(score.breakdown || []).map((b) => (
                      <div key={b.axis} data-testid={`foncier-score-axis-${b.axis}`}>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-600">{t(`radar.foncier.axis.${b.axis}`, { defaultValue: b.label })}</span>
                          <span className="font-semibold text-[#0F172A]">{b.points}/{b.max}</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100">
                          <div className="h-1.5 rounded-full bg-[#C6A87C]" style={{ width: `${Math.round((b.points / b.max) * 100)}%` }} />
                        </div>
                        {b.note && <p className="mt-0.5 text-[10px] text-slate-400">{b.note}</p>}
                      </div>
                    ))}
                  </div>
                  {score.disclaimer && <p className="mt-3 text-[10px] italic text-slate-400">{t("radar.foncier.scoreDisclaimer", { defaultValue: score.disclaimer })}</p>}
                </div>
              )}

              {/* 1 · Identification cadastrale + carte parcelle */}
              <Sec icon={Building2} title={t("radar.foncier.cadastre", "1 · Identification cadastrale")} testid="foncier-cadastre">
                <div className="grid gap-4 lg:grid-cols-2">
                  <div>
                    <KV label={t("radar.foncier.commune", "Commune")} value={cad.commune || listing.city} />
                    <KV label={t("radar.foncier.address", "Adresse")} value={listing.address || listing.geo_label} />
                    <KV label={t("radar.foncier.ref", "Référence cadastrale")} value={cad.reference} />
                    <KV label={t("radar.foncier.section", "Section / Parcelle")} value={cad.section || cad.numero ? `${cad.section || "—"} / ${cad.numero || "—"}` : null} />
                    <KV label={t("radar.foncier.surface", "Surface de la parcelle")} value={cad.contenance_m2 ? `${cad.contenance_m2.toLocaleString("fr-FR")} m²` : null} />
                    <KV label={t("radar.foncier.buildings", "Bâtiments sur la parcelle")} value={cad.buildings_count != null ? `${cad.buildings_count}${cad.buildings_source ? ` · ${cad.buildings_source}` : ""}` : null} />
                    <KV label={t("radar.foncier.footprint", "Emprise bâtie estimée")} value={cad.buildings_footprint_m2 ? `~ ${cad.buildings_footprint_m2.toLocaleString("fr-FR")} m²` : null} />
                  </div>
                  <div className="overflow-hidden rounded-xl border border-slate-200" style={{ minHeight: 260 }} data-testid="foncier-map">
                    {center ? (
                      <MapContainer center={center} zoom={18} scrollWheelZoom={false} style={{ height: 260, width: "100%" }}>
                        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        {rings.map((r, i) => (
                          <Polygon key={i} positions={r} pathOptions={{ color: "#C6A87C", weight: 2, fillColor: "#C6A87C", fillOpacity: 0.25 }} />
                        ))}
                        <Marker position={center} icon={parcelIcon} />
                      </MapContainer>
                    ) : (
                      <div className="flex h-full items-center justify-center p-6 text-center text-sm italic text-slate-400">{t("radar.foncier.noContour", "Contour de parcelle non disponible")}</div>
                    )}
                  </div>
                </div>
                {rings.length > 0 && <p className="mt-2 text-[11px] italic text-slate-400"><MapIcon className="mr-1 inline h-3 w-3" />{t("radar.foncier.contourSrc", "Contour parcellaire : Cadastre — API Carto IGN")}</p>}
              </Sec>

              {/* 2 · Urbanisme */}
              <Sec icon={ScrollText} title={t("radar.foncier.urbanisme", "2 · Urbanisme")} testid="foncier-urbanisme">
                <KV label={t("radar.foncier.duType", "Document applicable")} value={plu.du_type || (plu.rnu ? "RNU (Règlement National d'Urbanisme)" : null)} />
                <KV label={t("radar.foncier.zone", "Zone d'urbanisme")} value={plu.libelle || plu.typezone} />
                <KV label={t("radar.foncier.zoneLong", "Libellé de zone")} value={plu.libelong} />
                <div className="mt-3 flex flex-wrap gap-2">
                  {plu.reglement_pdf_url && (
                    <a href={plu.reglement_pdf_url} target="_blank" rel="noreferrer" data-testid="foncier-reglement">
                      <Button size="sm" variant="outline" className="rounded-full"><FileDown className="mr-1.5 h-3.5 w-3.5" />{t("radar.foncier.reglement", "Règlement écrit (PDF)")}</Button>
                    </a>
                  )}
                  {plu.document_page_url && (
                    <a href={plu.document_page_url} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline" className="rounded-full"><ExternalLink className="mr-1.5 h-3.5 w-3.5" />{t("radar.foncier.zoningPlan", "Plan de zonage (source officielle)")}</Button>
                    </a>
                  )}
                </div>

                {/* Règles principales — extraction IA (Option B) */}
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-sm font-medium text-[#0F172A]"><Sparkles className="h-4 w-4 text-[#C6A87C]" />{t("radar.foncier.rulesTitle", "Règles principales du PLU")}</span>
                    {!rules && (
                      <Button size="sm" onClick={loadRules} disabled={rulesLoading || !plu.reglement_pdf_url} className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="foncier-rules-btn">
                        {rulesLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}{t("radar.foncier.rulesBtn", "Extraire les règles (IA)")}
                      </Button>
                    )}
                  </div>
                  {!plu.reglement_pdf_url && <p className="mt-2 text-xs italic text-slate-400">{t("radar.foncier.noReglement", "Aucun règlement PDF disponible pour cette zone.")}</p>}
                  {rulesLoading && <p className="mt-2 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />{t("radar.foncier.rulesLoading", "Extraction assistée par IA en cours (20-40 s)…")}</p>}
                  {rules && rules.available && (
                    <>
                      <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800" data-testid="foncier-rules-disclaimer">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>{rules.disclaimer}</span>
                      </div>
                      <div className="mt-2 divide-y divide-slate-100">
                        {Object.entries(RULE_LABELS).map(([k, lab]) => {
                          const v = rules.rules?.[k] || {};
                          const impossible = /impossible/i.test(v.valeur || "");
                          return (
                            <div key={k} className="py-2" data-testid={`foncier-rule-${k}`}>
                              <div className="flex items-start justify-between gap-3">
                                <span className="text-xs font-medium text-slate-500">{lab}</span>
                                <span className="flex shrink-0 items-center gap-1">
                                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-amber-700">{t("radar.foncier.toVerify", "estimation à vérifier")}</span>
                                </span>
                              </div>
                              <p className={`mt-0.5 text-sm ${impossible ? "italic text-slate-400" : "text-[#0F172A]"}`}>{v.valeur || "—"}</p>
                              {v.reference && v.reference !== "Non trouvé" && <p className="text-[11px] text-slate-400">{t("radar.foncier.ruleRef", "Réf.")} : {v.reference}</p>}
                            </div>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-[10px] italic text-slate-400">{rules.method}</p>
                    </>
                  )}
                  {rules && !rules.available && <p className="mt-2 text-xs italic text-slate-400">{rules.reason || t("radar.foncier.rulesNa", "Règles non extractibles automatiquement.")}</p>}
                </div>
              </Sec>

              {/* 3 · Risques et contraintes */}
              <Sec icon={ShieldAlert} title={t("radar.foncier.risques", "3 · Risques et contraintes")} testid="foncier-risques">
                <KV label={t("radar.foncier.risquesRec", "Risques recensés (≤1 km)")} value={risk.risques_recenses == null ? null : (risk.risques_recenses.length ? risk.risques_recenses.join(", ") : t("radar.foncier.noneRec", "Aucun recensé au point"))} />
                <KV label={t("radar.foncier.argiles", "Retrait-gonflement argiles")} value={risk.argiles} />
                <KV label={t("radar.foncier.seisme", "Zonage sismique")} value={risk.sismique} />
                <KV label={t("radar.foncier.radon", "Potentiel radon")} value={risk.radon} />
                <KV label={t("radar.foncier.pollution", "Anciens sites industriels (≤500 m)")} value={risk.sites_pollues_count == null ? null : risk.sites_pollues_count} />
                <KV label={t("radar.foncier.icpe", "Installations classées ICPE (≤1 km)")} value={risk.icpe_count == null ? null : risk.icpe_count} />
                <KV label={t("radar.foncier.sup", "Servitudes d'utilité publique")} value={sup.sup && sup.sup.length ? sup.sup.slice(0, 6).join(", ") + (sup.sup.length > 6 ? "…" : "") : (sup.available ? t("radar.foncier.noneRec", "Aucun recensé au point") : null)} />
                <KV label={t("radar.foncier.abf", "Périmètre ABF / Monument")} value={sup.abf ? t("radar.foncier.yes", "Oui — avis ABF probablement requis") : (sup.available ? t("radar.foncier.no", "Non détecté au point") : null)} />
              </Sec>

              {/* 4 · Potentiel */}
              <Sec icon={Ruler} title={t("radar.foncier.potentiel", "4 · Potentiel du bien")} testid="foncier-potentiel">
                <div className="mb-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-[11px] italic text-slate-500">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#C6A87C]" />{pot.disclaimer}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <PotPill label={t("radar.foncier.division", "Division parcellaire")} level={pot.division} />
                  <PotPill label={t("radar.foncier.extension", "Extension")} level={pot.extension} />
                  <PotPill label={t("radar.foncier.surel", "Surélévation")} level={pot.surelevation} />
                  <PotPill label={t("radar.foncier.neuf", "Construction neuve")} level={pot.construction_neuve} />
                </div>
                {pot.surface_libre_estimee_m2 && <p className="mt-2 text-sm text-slate-600">{t("radar.foncier.libre", "Surface libre estimée")} : <b>{pot.surface_libre_estimee_m2.toLocaleString("fr-FR")} m²</b></p>}
                {pot.points_favorables?.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1 flex items-center gap-1.5 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" />{t("radar.foncier.favor", "Points favorables")}</p>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">{pot.points_favorables.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {pot.points_vigilance?.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1 flex items-center gap-1.5 text-sm font-medium text-amber-700"><AlertTriangle className="h-4 w-4" />{t("radar.foncier.vigil", "Points de vigilance")}</p>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">{pot.points_vigilance.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
              </Sec>

              {/* 5 · Sources */}
              <Sec icon={Info} title={t("radar.foncier.sources", "5 · Sources officielles")} testid="foncier-sources">
                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">{(data.sources || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
                {data.generated_at && <p className="mt-2 text-[11px] text-slate-400">{t("radar.foncier.generated", "Données consultées le")} {data.generated_at.slice(0, 10)}</p>}
                <p className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-[11px] leading-relaxed text-slate-500">
                  <b>{t("radar.foncier.warnTitle", "Avertissement")} :</b> {t("radar.foncier.warn", "Cette analyse est une aide à la décision issue de sources publiques officielles. Elle ne constitue ni un avis juridique, ni une garantie de constructibilité. Les règles extraites par IA sont des estimations à vérifier. Toute information doit être confirmée auprès des services compétents (mairie, service urbanisme, ABF, géomètre, notaire).")}
                </p>
              </Sec>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
